import { motion } from "framer-motion";
import { Target, Eye, Heart, Building2 } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import { VALUES } from "@/data/content";

export default function About() {
  return (
    <div data-testid="about-page" className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 grid-bg radial-fade opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 relative">
          <p className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#00D4FF] mb-4">
            About TriNexa
          </p>
          <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight text-white max-w-3xl leading-[1.05]">
            Engineering the <span className="text-gradient-cyan">future</span>, one intelligent system at a time.
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
            We are a Bangalore-based collective of senior engineers, data
            scientists and product designers — obsessed with shipping
            production-grade software that uses AI to compound business value.
          </p>
        </div>
      </section>

      {/* STORY */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative aspect-[5/6] rounded-3xl overflow-hidden border border-white/10"
          >
            <img
              src="https://images.unsplash.com/photo-1775709610612-c589e29679ea?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80"
              alt="Modern office"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-transparent to-transparent" />
          </motion.div>

          <div>
            <p className="font-mono-tech text-xs tracking-[0.3em] uppercase text-[#00D4FF] mb-3">
              Our Story
            </p>
            <h2 className="font-display text-4xl font-bold tracking-tight text-white mb-6">
              Built by engineers who care.
            </h2>
            <div className="space-y-5 text-slate-300 leading-relaxed">
              <p>
                TriNexa was founded with a simple belief — that the next decade
                of software will be defined by teams that can blend AI,
                rigorous data engineering and modern cloud craft into one
                fluent practice.
              </p>
              <p>
                We work shoulder-to-shoulder with founders and enterprise
                leaders to translate ambitious ideas into resilient,
                production-grade systems. Our work has powered AI products,
                modernized data platforms, and accelerated engineering teams
                across continents.
              </p>
              <p>
                We are based in Bangalore, but our work has global reach. Our
                operating principle is simple: do the work, ship the outcome.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION / VISION */}
      <section className="py-24 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-6">
          <div data-testid="about-mission" className="p-8 rounded-2xl bg-[#0A1128] border border-white/10 hover-glow">
            <div className="w-12 h-12 rounded-lg bg-[#00D4FF]/10 border border-[#00D4FF]/30 flex items-center justify-center mb-5">
              <Target className="text-[#00D4FF]" size={22} />
            </div>
            <h3 className="font-display text-2xl font-bold text-white mb-3">Mission</h3>
            <p className="text-slate-300 leading-relaxed">
              To help ambitious teams ship intelligent software — turning data,
              AI and cloud-native engineering into measurable business
              outcomes.
            </p>
          </div>
          <div data-testid="about-vision" className="p-8 rounded-2xl bg-[#0A1128] border border-white/10 hover-glow">
            <div className="w-12 h-12 rounded-lg bg-[#00D4FF]/10 border border-[#00D4FF]/30 flex items-center justify-center mb-5">
              <Eye className="text-[#00D4FF]" size={22} />
            </div>
            <h3 className="font-display text-2xl font-bold text-white mb-3">Vision</h3>
            <p className="text-slate-300 leading-relaxed">
              A world where every product is data-aware, AI-augmented, and
              engineered with the rigour of modern platforms — accessible to
              teams of every size.
            </p>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeader
            eyebrow="What we stand for"
            title="Our values."
            description="Principles that shape how we hire, design, build and partner."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                data-testid={`value-card-${i}`}
                className="p-6 rounded-2xl bg-[#0A1128] border border-white/10 hover-glow"
              >
                <Heart className="text-[#00D4FF] mb-4" size={20} />
                <h4 className="font-display text-lg font-semibold text-white mb-2">
                  {v.title}
                </h4>
                <p className="text-sm text-slate-400 leading-relaxed">{v.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HQ */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <Building2 className="text-[#00D4FF] mx-auto mb-4" size={28} />
          <h3 className="font-display text-3xl font-bold text-white">
            Headquartered in Bangalore.
          </h3>
          <p className="mt-3 text-slate-400 max-w-xl mx-auto">
            India's tech capital — a city that breathes engineering, design and
            ambition. We work with partners across India, the US, Europe and
            the Middle East.
          </p>
        </div>
      </section>
    </div>
  );
}
